import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- TODO(ENG-668): migrate to ServerDataGrid; DataGridPro kept direct for now
import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { fetchContactsAPI } from '@sophocles/api/contacts';
import PrimaryContactConfirmDialog from '@sophocles/components/contacts/PrimaryContactConfirmDialog';
import UsageHistoryView from '@sophocles/components/contacts/UsageHistoryView';
import useContactUsageHistory from '@sophocles/components/contacts/useContactUsageHistory';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { deleteContact, restoreContact } from '@sophocles/features/contacts/api';
import { addToast } from '@sophocles/features/toast';
import { buildContactQueryParams } from '@sophocles/features/warehouse/contactQueryParams';
import { formatPhoneDisplay } from '@sophocles/utils/phoneUtils';

/**
 * ContactGrid - Desktop view with MUI DataGrid and server-side pagination
 */
const ContactGrid = ({
  customerId,
  siteGroupId,
  showDeleted,
  onEditContact,
  scopeFilter = 'all',
  searchQuery = '',
  filters = {},
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);

  // Primary contact dialog state
  const [primaryDialogOpen, setPrimaryDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Row selection state for bulk delete/restore (v8 format)
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const selectedIds = useMemo(() => Array.from(rowSelectionModel.ids || []), [rowSelectionModel]);
  // Track if we're selecting deleted or non-deleted contacts (determined by first selection)
  const [selectionMode, setSelectionMode] = useState(null); // 'deleted' | 'active' | null

  // Handle row selection changes - track selection mode based on first selected row
  const handleRowSelectionChange = useCallback(
    newSelection => {
      const newIds = Array.from(newSelection.ids || []);
      if (newIds.length === 0) {
        // Clear selection mode when all items are deselected
        setSelectionMode(null);
        setRowSelectionModel({ type: 'include', ids: new Set() });
      } else if (selectedIds.length === 0 && newIds.length > 0) {
        // First selection - determine mode based on the selected row's deleted status
        const firstSelectedId = newIds[0];
        const firstSelectedRow = rows.find(r => r.id === firstSelectedId);
        if (firstSelectedRow) {
          const isDeleted = !!firstSelectedRow.deleted_at;
          setSelectionMode(isDeleted ? 'deleted' : 'active');
        }
        setRowSelectionModel(newSelection);
      } else {
        setRowSelectionModel(newSelection);
      }
    },
    [selectedIds.length, rows],
  );

  // Bulk delete dialog state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkRestoreDialogOpen, setBulkRestoreDialogOpen] = useState(false);

  // Handle bulk delete - exposed via ref or callback
  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  }, [selectedIds.length]);

  // Handle bulk restore - exposed via ref or callback
  const handleBulkRestoreClick = useCallback(() => {
    if (selectedIds.length === 0) return;
    setBulkRestoreDialogOpen(true);
  }, [selectedIds.length]);

  // Notify parent of selection changes and provide delete/restore handlers
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedIds, handleBulkDeleteClick, handleBulkRestoreClick, selectionMode);
    }
  }, [selectedIds, onSelectionChange, handleBulkDeleteClick, handleBulkRestoreClick, selectionMode]);

  // Usage history dialog state
  const usageHistory = useContactUsageHistory();

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  // Helper function to determine contact scope
  const getContactScope = useCallback(row => {
    // Customer scope: has customer FK but NO site_group FK
    if (row.customer && !row.site_group) {
      return 'customer';
    }
    // Site Group scope: has site_group FK
    if (row.site_group) {
      return 'site_group';
    }
    return 'unknown';
  }, []);

  // Helper function to check if contact is inherited (customer-level when viewing from site group)
  const isInheritedContact = useCallback(
    row => {
      return siteGroupId && scopeFilter === 'all' && getContactScope(row) === 'customer';
    },
    [siteGroupId, scopeFilter, getContactScope],
  );

  // Load contacts function
  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        not_deleted: !showDeleted,
        ...buildContactQueryParams(filters, { customerId, siteGroupId, scopeFilter, searchQuery }),
      };

      // Add sorting (backend uses 'o' param with format: o=-field for desc, o=field for asc)
      if (sortModel.length > 0) {
        const sort = sortModel[0];
        params.o = sort.sort === 'desc' ? `-${sort.field}` : sort.field;
      }

      // Fetch contacts with single API call
      const response = await fetchContactsAPI(params);
      const allContacts = response.results || response;

      // Set results - append for infinite scroll, replace for page 0
      if (paginationModel.page === 0) {
        setRows(allContacts);
      } else {
        setRows(prev => [...prev, ...allContacts]);
      }
      setRowCount(response.count || allContacts.length);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      dispatch(addToast({ message: t('contacts.management.loadError', 'Failed to load contacts'), level: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [
    customerId,
    siteGroupId,
    showDeleted,
    scopeFilter,
    paginationModel,
    sortModel,
    searchQuery,
    filters,
    t,
    dispatch,
  ]);

  // Reload contacts when dependencies change
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Infinite scroll: Load more when scrolling near bottom
  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      // Check if we're within 300px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 300 && !loading) {
        const hasMoreRows = rows.length < rowCount;
        if (hasMoreRows) {
          // Load next page
          setPaginationModel(prev => ({ ...prev, page: prev.page + 1 }));
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, rows.length, rowCount]);

  // Handle primary star click
  const handlePrimaryClick = (contact, primaryType) => {
    setSelectedContact({ ...contact, primaryType });
    setPrimaryDialogOpen(true);
  };

  const handlePrimaryDialogClose = () => {
    setPrimaryDialogOpen(false);
    setSelectedContact(null);
  };

  const handlePrimarySuccess = () => {
    loadContacts(); // Reload to reflect changes
    handlePrimaryDialogClose();
  };

  // Open delete confirmation dialog
  const handleDeleteClick = contact => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  // Handle delete (after confirmation)
  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    try {
      await dispatch(deleteContact(contactToDelete.id)).unwrap();
      dispatch(addToast({ message: t('contacts.management.deleteSuccess'), level: 'success' }));
      loadContacts();
      handleDeleteCancel();
    } catch (error) {
      dispatch(addToast({ message: t('contacts.management.deleteError'), level: 'error' }));
    }
  };

  // Handle restore
  const handleRestore = async contactId => {
    try {
      await dispatch(restoreContact(contactId)).unwrap();
      dispatch(addToast({ message: t('contacts.management.restoreSuccess'), level: 'success' }));
      loadContacts();
    } catch (error) {
      dispatch(addToast({ message: t('contacts.management.restoreError'), level: 'error' }));
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (selectedIds.length === 0) return;

    try {
      // Batch delete
      await Promise.all(selectedIds.map(id => dispatch(deleteContact(id)).unwrap()));

      dispatch(
        addToast({
          message: t('contacts.management.bulkDeleteSuccess', 'Delete {{count}} contact(s)?', {
            count: selectedIds.length,
          }),
          level: 'success',
        }),
      );

      setRowSelectionModel({ type: 'include', ids: new Set() });
      setBulkDeleteDialogOpen(false);
      loadContacts();
    } catch (error) {
      dispatch(
        addToast({ message: t('contacts.management.bulkDeleteError', 'Failed to delete contacts'), level: 'error' }),
      );
    }
  }, [selectedIds, dispatch, t, loadContacts]);

  const handleBulkRestoreCancel = () => {
    setBulkRestoreDialogOpen(false);
  };

  const handleBulkRestoreConfirm = useCallback(async () => {
    if (selectedIds.length === 0) return;

    try {
      // Batch restore
      await Promise.all(selectedIds.map(id => dispatch(restoreContact(id)).unwrap()));

      dispatch(
        addToast({
          message: t('contacts.management.bulkRestoreSuccess', 'Restored {{count}} contact(s)', {
            count: selectedIds.length,
          }),
          level: 'success',
        }),
      );

      setRowSelectionModel({ type: 'include', ids: new Set() });
      setBulkRestoreDialogOpen(false);
      loadContacts();
    } catch (error) {
      dispatch(
        addToast({ message: t('contacts.management.bulkRestoreError', 'Failed to restore contacts'), level: 'error' }),
      );
    }
  }, [selectedIds, dispatch, t, loadContacts]);

  // Define columns — single rich-formatted "Contact" column stacks
  // identity, primary-role chips, email/phone, and address. Inline
  // editing is handled by the row-level Edit dialog, not the grid.
  const columns = [
    {
      field: 'name',
      headerName: t('contacts.fields.contact', 'Contact'),
      flex: 1,
      minWidth: 360,
      renderCell: params => {
        const row = params.row;
        const isInherited = isInheritedContact(row);
        const displayName = row.name || row.business_name || '—';
        const secondaryText = row.name && row.business_name && row.business_name.trim() ? row.business_name : null;

        const cityLine = [row.city, row.state].filter(Boolean).join(', ');
        const addressLine = [cityLine, row.zipcode].filter(Boolean).join(' ');
        const fullAddress = [row.address, addressLine, row.country].filter(Boolean).join(' • ');

        const hasRmaPrimary = row.is_primary_rma_billing || row.is_primary_rma_shipping;
        const hasSalesPrimary = row.is_primary_sales_billing || row.is_primary_sales_shipping;
        const hasChips = hasRmaPrimary || hasSalesPrimary || isInherited;

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {displayName}
              </Typography>
              {secondaryText && (
                <Typography variant="caption" color="text.secondary">
                  {secondaryText}
                </Typography>
              )}
            </Box>

            {hasChips && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {row.is_primary_rma_billing && (
                  <Chip
                    label={t('contacts.primary.rmaBilling', 'RMA Billing')}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: 'warning.main',
                      color: 'warning.contrastText',
                    }}
                  />
                )}
                {row.is_primary_rma_shipping && (
                  <Chip
                    label={t('contacts.primary.rmaShipping', 'RMA Shipping')}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: 'warning.main',
                      color: 'warning.contrastText',
                    }}
                  />
                )}
                {row.is_primary_sales_billing && (
                  <Chip
                    label={t('contacts.primary.salesBilling', 'Sales Billing')}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: 'success.main',
                      color: 'success.contrastText',
                    }}
                  />
                )}
                {row.is_primary_sales_shipping && (
                  <Chip
                    label={t('contacts.primary.salesShipping', 'Sales Shipping')}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: 'success.main',
                      color: 'success.contrastText',
                    }}
                  />
                )}
                {isInherited && (
                  <Chip
                    label={t('contacts.scope.inherited', 'Inherited')}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            )}

            {(row.email || row.phone1) && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.25 }}
                useFlexGap
              >
                {row.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <EmailIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" noWrap>
                      {row.email}
                    </Typography>
                  </Box>
                )}
                {row.phone1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption">{formatPhoneDisplay(row.phone1)}</Typography>
                  </Box>
                )}
              </Stack>
            )}

            {fullAddress && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: 'text.secondary' }}>
                <PlaceIcon sx={{ fontSize: 14, mt: '2px' }} />
                <Typography variant="caption">{fullAddress}</Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'deleted_at',
      headerName: t('contacts.fields.status', 'Status'),
      width: 100,
      sortable: false,
      renderCell: params => {
        if (params.value) {
          return <GridStatusCell label={t('contacts.status.deleted', 'Deleted')} color="bad" />;
        }
        return null;
      },
    },
    {
      field: 'actions',
      headerName: t('actions.actions', 'Actions'),
      width: 160,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: params => {
        const isDeleted = !!params.row.deleted_at;
        const isInherited = isInheritedContact(params.row);

        // Disable edit/delete/restore for inherited contacts
        if (isInherited) {
          return (
            <Tooltip
              title={t(
                'contacts.scope.inheritedTooltip',
                'This contact is inherited from the customer level and cannot be edited here',
              )}
            >
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <span>
                  <IconButton size="small" disabled>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
                <span>
                  <IconButton size="small" disabled>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {/* Usage History Button */}
            <Tooltip title={t('contacts.usageHistory.viewHistory', 'View History')}>
              <IconButton size="small" onClick={() => usageHistory.open(params.row)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {!isDeleted && (
              <>
                <Tooltip title={t('actions.edit', 'Edit')}>
                  <IconButton size="small" onClick={() => onEditContact(params.row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('actions.delete', 'Delete')}>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {isDeleted && (
              <Tooltip title={t('actions.restore', 'Restore')}>
                <IconButton size="small" color="primary" onClick={() => handleRestore(params.row.id)}>
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          hideFooter
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          checkboxSelection
          isRowSelectable={params => {
            // Inherited contacts are never selectable
            if (isInheritedContact(params.row)) return false;
            // If no selection yet, all non-inherited rows are selectable
            if (selectionMode === null) return true;
            // Once selection starts, only allow same type (deleted or active)
            const isDeleted = !!params.row.deleted_at;
            return selectionMode === 'deleted' ? isDeleted : !isDeleted;
          }}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleRowSelectionChange}
          disableRowSelectionOnClick
          autoHeight
          getRowHeight={() => 'auto'}
          getRowClassName={params => {
            // Visual distinction for inherited contacts
            return isInheritedContact(params.row) ? 'inherited-contact-row' : '';
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              py: 1,
              alignItems: 'flex-start',
            },
            '& .inherited-contact-row': {
              opacity: 0.75,
              fontStyle: 'italic',
            },
          }}
        />
      </Box>

      {/* Primary Contact Dialog */}
      <PrimaryContactConfirmDialog
        open={primaryDialogOpen}
        contact={selectedContact}
        onClose={handlePrimaryDialogClose}
        onSuccess={handlePrimarySuccess}
      />

      {/* Usage History Dialog */}
      <Dialog
        open={usageHistory.isOpen}
        onClose={usageHistory.close}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">{t('contacts.usageHistory.title', 'Usage History')}</Typography>
          </Box>
          {usageHistory.contact && (
            <Typography variant="body2" color="text.secondary">
              {usageHistory.contact.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {usageHistory.contact && (
            <UsageHistoryView
              usageData={usageHistory.usageData}
              loading={usageHistory.loading}
              error={usageHistory.usageData?.error}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={usageHistory.close}>{t('actions.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (Single) */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('contacts.management.deleteConfirmTitle', 'Delete {{count}} contact(s)?', { count: 1 })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'contacts.management.deleteConfirmMessage',
              'Are you sure you want to delete this contact? This will mark the contact as deleted.',
            )}
          </Typography>
          {contactToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2">{contactToDelete.name}</Typography>
              {contactToDelete.email && (
                <Typography variant="body2" color="text.secondary">
                  {contactToDelete.email}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('actions.cancel', 'Cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('actions.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('contacts.management.deleteConfirmTitle', 'Delete {{count}} contact(s)?', {
            count: selectedIds.length,
          })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'contacts.management.bulkDeleteConfirmMessage',
              'Are you sure you want to delete {{count}} contact(s)? This will mark them as deleted.',
              { count: selectedIds.length },
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>{t('actions.cancel', 'Cancel')}</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            {t('actions.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Restore Confirmation Dialog */}
      <Dialog open={bulkRestoreDialogOpen} onClose={handleBulkRestoreCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('contacts.management.restoreConfirmTitle', 'Restore {{count}} contact(s)?', {
            count: selectedIds.length,
          })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'contacts.management.bulkRestoreConfirmMessage',
              'Are you sure you want to restore {{count}} contact(s)?',
              { count: selectedIds.length },
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkRestoreCancel}>{t('actions.cancel', 'Cancel')}</Button>
          <Button onClick={handleBulkRestoreConfirm} color="primary" variant="contained">
            {t('actions.restore', 'Restore')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ContactGrid.propTypes = {
  customerId: PropTypes.number,
  siteGroupId: PropTypes.number,
  showDeleted: PropTypes.bool.isRequired,
  onEditContact: PropTypes.func.isRequired,
  scopeFilter: PropTypes.oneOf(['all', 'customer', 'site_group']),
  searchQuery: PropTypes.string,
  filters: PropTypes.object,
  onSelectionChange: PropTypes.func,
};

export default ContactGrid;
